import * as _ from 'lodash';
import { stringNormalize } from 'quill-string-normalizer';
import * as React from "react";
import { connect } from "react-redux";
import * as Redux from "redux";
import { sentences } from 'sbd';
import Pusher from 'pusher-js';

import EarlySubmitModal from './earlySubmitModal';
import FollowupModal from './followupModal';
import formatInitialPassage from './formatInitialPassage';
import Paragraph from './paragraph';
import PassageReviewer from './passageReviewer';
import ProgressBar from './progressBar';
import ResetModal from './resetModal';
import ReviewModal from './reviewModal';
import WelcomePage from './welcomePage';
import { ReadAloudNote, ReadAloudPassageRegion } from './readAloudPassage';

import { ReadAloudSource } from '../../../Shared/components/readAloud/readAloudSource';
import { CLIP_SOURCE_TYPE, clipSource } from '../../../Shared/libs/speech/clipSource';
import { requestPost, requestPut, } from '../../../../modules/request/index';
import { sumTimeTracking } from '../../../Shared/utils/liveViewAPIs';
import { LiveViewProgress, createLiveViewProgress } from '../../../Shared/utils/liveViewProgress';
import {
  CLICK,
  KEYDOWN,
  KEYPRESS,
  MOUSEDOWN,
  MOUSEMOVE,
  SCROLL,
  VISIBILITYCHANGE,
  getParameterByName,
  roundValuesToSeconds,
  SMALL_ICON_BASE_SRC,
} from '../../../Shared/index';
import { startListeningToConcepts } from "../../actions/concepts";
import { getActivity } from "../../actions/proofreaderActivities";
import {
  setPassage,
  setSessionReducerToSavedSession,
  updateConceptResultsOnFirebase,
  updateSessionOnFirebase,
  updateTimeTracking
} from "../../actions/session";
import EditCaretPositioning from '../../../Shared/libs/editCaretPositioning';
import determineUnnecessaryEditType, { UNNECESSARY_SPACE, unnecessarySpaceSplitResponse, } from '../../helpers/determineUnnecessaryEditType';
import { joinWords } from '../../helpers/joinWords';
import { ReadAloudContent } from '../../../Shared/components/readAloud/readAloudContent';
import { ReadAloudHtml } from '../../../Shared/components/readAloud/readAloudHtml';
import { ConceptResultObject, WordObject } from '../../interfaces/proofreaderActivities';
import { checkIfGrammarQuestionsExistForProofreaderSession } from '../../../Shared/libs/proofreaderToGrammarHelpers';
import { Spinner } from '../../../Shared/index';
import { ProofreaderActivityState } from '../../reducers/proofreaderActivitiesReducer';
import { SessionState } from '../../reducers/sessionReducer';

interface PlayProofreaderContainerProps {
  proofreaderActivities: ProofreaderActivityState;
  session: SessionState;
  dispatch: Function;
  admin?: Boolean;
  activityUID?: string;
  concepts?: any;
  setHasPassageButtons: () => void
}

interface PlayProofreaderContainerState {
  edits: number;
  reviewing: boolean;
  numberOfResets: number;
  showEarlySubmitModal: boolean;
  showReviewModal: boolean;
  showResetModal: boolean;
  showFollowupModal: boolean;
  checkingGrammarQuestions: boolean;
  showWelcomePage: boolean;
  firebaseSessionID: string | null;
  loadingFirebaseSession: boolean;
  currentActivity: any;
  necessaryEdits?: RegExpMatchArray | null;
  numberOfCorrectChanges?: number;
  originalPassage?: Array<Array<WordObject>>;
  reviewablePassage?: string;
  conceptResultsObjects?: ConceptResultObject[];
  startTime: number;
  isIdle: boolean;
  inactivityTimer?: NodeJS.Timeout;
}

const FIREBASE_SAVE_INTERVAL = 30000 // 30 seconds
export const DEFAULT_INSTRUCTIONS = 'Find and correct the errors in the passage. To edit a word, click on it and re-type it.'

const editCount = (paragraphs: Array<Array<any>>) => {
  let editCount = 0
  paragraphs.forEach((p: Array<any>) => {
    const changedWords = p.filter(word => word.currentText !== word.originalText)
    editCount += changedWords.length
  })
  return editCount
}

export const findSentence = (paragraphSentences: string[], wordIndex: number, word: string) => {
  let cumulativeWordIndex = 0;

  for (const paragraphSentence of paragraphSentences) {
    const wordsInSentence = paragraphSentence.split(/\s+/);
    const sentenceWordCount = wordsInSentence.length;
    const indexOfFirstWordInSentence = cumulativeWordIndex;
    const indexOfLastWordInSentence = cumulativeWordIndex + sentenceWordCount - 1;

    if (wordIndex >= indexOfFirstWordInSentence && wordIndex <= indexOfLastWordInSentence && paragraphSentence.includes(word)) {
      return paragraphSentence.replace(word, `<strong>${word}</strong>`);
    }

    cumulativeWordIndex += sentenceWordCount;
  }

  return '';
};


const joinParagraph = (paragraph: Array<any>) => {
  const normalizedWords = paragraph.map(w => stringNormalize(w.originalText))
  return joinWords(normalizedWords)
}

export class PlayProofreaderContainer extends React.Component<PlayProofreaderContainerProps, PlayProofreaderContainerState> {
  static getDerivedStateFromProps(nextProps: PlayProofreaderContainerProps, prevState: PlayProofreaderContainerState) {
    const { proofreaderActivities, session, dispatch, } = nextProps
    const theCurrentActivityHasNotChanged = _.isEqual(proofreaderActivities.currentActivity, prevState.currentActivity)
    if ((session.passage && theCurrentActivityHasNotChanged && prevState.necessaryEdits) || !proofreaderActivities.currentActivity) { return null }

    const { passage, questions } = proofreaderActivities.currentActivity
    const initialPassageData = formatInitialPassage(passage, questions)
    const formattedPassage = initialPassageData.passage
    let currentPassage = formattedPassage
    if (session.passageFromFirebase && typeof session.passageFromFirebase !== 'string' && session.passageFromFirebase.length) {
      currentPassage = session.passageFromFirebase
    }

    if (initialPassageData.necessaryEdits) {
      dispatch(setPassage(currentPassage))
    }

    return { originalPassage: _.cloneDeep(formattedPassage), necessaryEdits: initialPassageData.necessaryEdits, edits: editCount(currentPassage), currentActivity: proofreaderActivities.currentActivity, loadingFirebaseSession: false }
  }

  private interval: any // eslint-disable-line react/sort-comp
  private liveViewProgress: LiveViewProgress|null

  constructor(props: any) {
    super(props);

    this.passageContainer = null

    const { proofreaderActivities, admin, } = props

    const { currentActivity, } = proofreaderActivities

    const firebaseSessionID = getParameterByName('student', window.location.href)

    if (firebaseSessionID) {
      props.dispatch(setSessionReducerToSavedSession(firebaseSessionID, true))
      this.interval = setInterval(() => {
        this.saveEditedSessionToFirebase(firebaseSessionID)
      }, FIREBASE_SAVE_INTERVAL)
      // Proofreader has no discrete question flow, so live view only gets
      // start + presence; prompt completions are not reported
      this.liveViewProgress = createLiveViewProgress(firebaseSessionID, {
        totalPrompts: null,
        getTimeSpentMs: () => {
          const { session, } = this.props
          return sumTimeTracking(session.timeTracking)
        },
        getStage: () => 'proofreading',
        // Reporting starts here in the constructor, before state exists
        getIdle: () => {
          const { isIdle, } = this.state || { isIdle: false, }
          return isIdle
        },
      })
    }

    this.state = {
      edits: 0,
      reviewing: false,
      showEarlySubmitModal: false,
      showReviewModal: false,
      showResetModal: false,
      showFollowupModal: false,
      showWelcomePage: !admin,
      numberOfResets: 0,
      loadingFirebaseSession: !!firebaseSessionID,
      firebaseSessionID,
      currentActivity,
      startTime: Date.now(),
      isIdle: false,
      checkingGrammarQuestions: false,
    }
  }

  activityUid = () => {
    const { activityUID } = this.props
    return getParameterByName('uid', window.location.href) || activityUID
  }

  componentDidMount() {
    const { dispatch } = this.props
    const activityUIDToUse = this.activityUid()
    dispatch(startListeningToConcepts())

    if (activityUIDToUse) {
      dispatch(getActivity(activityUIDToUse))
    }

    window.addEventListener(KEYDOWN, this.resetTimers)
    window.addEventListener(MOUSEMOVE, this.resetTimers)
    window.addEventListener(MOUSEDOWN, this.resetTimers)
    window.addEventListener(CLICK, this.resetTimers)
    window.addEventListener(KEYPRESS, this.resetTimers)
    window.addEventListener(SCROLL, this.resetTimers)
    window.addEventListener(VISIBILITYCHANGE, this.setIdle)
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.liveViewProgress) { this.liveViewProgress.reportState() }

    const { proofreaderActivities, setHasPassageButtons } = this.props
    const { showWelcomePage } = this.state
    const { currentActivity, hasreceiveddata } = proofreaderActivities

    if (prevProps.proofreaderActivities.hasreceiveddata !== hasreceiveddata && hasreceiveddata) {
      document.title = `Quill.org | ${currentActivity.title}`
    }
    if(prevState.showWelcomePage && !showWelcomePage) {
      setHasPassageButtons()
    }
  }

  componentWillUnmount() {
    if (this.interval) {
      clearInterval(this.interval)
    }

    if (this.liveViewProgress) { this.liveViewProgress.stop() }

    window.removeEventListener(KEYDOWN, this.resetTimers)
    window.removeEventListener(MOUSEMOVE, this.resetTimers)
    window.removeEventListener(MOUSEDOWN, this.resetTimers)
    window.removeEventListener(CLICK, this.resetTimers)
    window.removeEventListener(KEYPRESS, this.resetTimers)
    window.removeEventListener(SCROLL, this.resetTimers)
    window.removeEventListener(VISIBILITYCHANGE, this.setIdle)
  }

  private passageContainer: HTMLDivElement | null = null

  resetTimers = (e = null) => {
    const { session, dispatch, } = this.props

    if (!session) { return }

    const now = Date.now()
    this.setState((prevState, props) => {
      const { timeTracking, } = props.session

      const { startTime, isIdle, inactivityTimer, } = prevState

      if (inactivityTimer) { clearTimeout(inactivityTimer) }

      let elapsedTime = now - startTime

      if (isIdle) {
        elapsedTime = 0
      }
      dispatch(updateTimeTracking({ ...timeTracking, 'proofreading_the_passage': (timeTracking && timeTracking['proofreading_the_passage'] || 0) + elapsedTime }))
      const newInactivityTimer = setTimeout(this.setIdle, 30000);  // time is in milliseconds (1000 is 1 second)

      return { isIdle: false, inactivityTimer: newInactivityTimer, startTime: now, }
    })

    return Promise.resolve(true);
  }

  setIdle = () => { this.resetTimers().then(() => this.setState({ isIdle: true })) }

  saveEditedSessionToFirebase = (sessionID: string) => {
    const { dispatch, session, } = this.props
    const { passageFromFirebase, passage, timeTracking, } = session
    if (!_.isEqual(passage, passageFromFirebase)) {
      dispatch(updateSessionOnFirebase(sessionID, { passage, timeTracking, }))
    }
  }

  saveCompletedSessionToFirebase = () => {
    const { firebaseSessionID, conceptResultsObjects, } = this.state
    const { session, } = this.props
    const { passage, timeTracking, } = session
    const activityUID = getParameterByName('uid', window.location.href)
    const newOrSetFirebaseSessionID = updateConceptResultsOnFirebase(firebaseSessionID, activityUID, { conceptResults: conceptResultsObjects, timeTracking, passage, })
    this.setState({ firebaseSessionID: newOrSetFirebaseSessionID })
  }

  defaultInstructions = () => {
    return DEFAULT_INSTRUCTIONS
  }

  saveToLMS = () => {
    const { firebaseSessionID, conceptResultsObjects, } = this.state
    const { session, } = this.props

    const sessionID = getParameterByName('student', window.location.href)
    const results: ConceptResultObject[] | undefined = conceptResultsObjects;
    if (results) {
      const score = this.calculateScoreForLesson();
      const activityUID = getParameterByName('uid', window.location.href)

      const data = { time_tracking: roundValuesToSeconds(session.timeTracking), }

      if (sessionID) {
        this.handleCheckWorkClickSession(sessionID, results, score, data);
      } else if (activityUID) {
        this.createAnonActivitySession(activityUID, results, score, firebaseSessionID, data);
      }
    }
  }

  calculateScoreForLesson = () => {
    const { numberOfCorrectChanges, necessaryEdits } = this.state
    if (numberOfCorrectChanges && necessaryEdits && necessaryEdits.length) {
      return numberOfCorrectChanges / necessaryEdits.length
    } else {
      return 0
    }
  }

  initializeSubscription(activitySessionUid) {
    if (window.postbuildEnv.NODE_ENV === 'development') {
      Pusher.logToConsole = true;
    }
    if (!window.pusher) {
      window.pusher = new Pusher(window.postbuildEnv.PUSHER_KEY, { cluster: window.postbuildEnv.PUSHER_CLUSTER });
    }
    const channel = window.pusher.subscribe(activitySessionUid);
    channel.bind('concept-results-saved', () => {
      document.location.href = `${window.postbuildEnv.DEFAULT_URL}/activity_sessions/${activitySessionUid}`;
    });

    channel.bind('concept-results-partially-saved', () => {
      document.location.href = window.postbuildEnv.DEFAULT_URL;
    });
  }

  handleCheckWorkClickSession = (sessionID: string, results: ConceptResultObject[], score: number, data) => {
    this.initializeSubscription(sessionID)
    requestPut(
      `${window.postbuildEnv.DEFAULT_URL}/api/v1/activity_sessions/${sessionID}`,
      {
        state: 'finished',
        concept_results: results,
        percentage: score,
        data
      },
      (body) => {
        // not doing anything here because Pusher should handle the redirect once the concept results are saved
      }
    )
  }

  createAnonActivitySession = (lessonID: string, results: ConceptResultObject[], score: number, sessionID: string | null, data) => {
    requestPost(
      `${window.postbuildEnv.DEFAULT_URL}/api/v1/activity_sessions/`,
      {
        state: 'finished',
        activity_uid: lessonID,
        concept_results: results,
        percentage: score,
        data
      },
      (body) => {
        this.initializeSubscription(body.activity_session.uid)
      }
    )

  }

  checkWork = (): { reviewablePassage: string, numberOfCorrectChanges: number, conceptResultsObjects: ConceptResultObject[] } => {
    const { session, proofreaderActivities, } = this.props
    const { passage } = session
    const { currentActivity } = proofreaderActivities
    const { necessaryEdits, } = this.state
    let numberOfCorrectChanges = 0
    const conceptResultsObjects: ConceptResultObject[] = []
    if (passage && necessaryEdits) {
      let reviewablePassage = ''
      passage.forEach((paragraph: Array<any>) => {
        const originalParagraphString = joinParagraph(paragraph)
        const paragraphSentences = sentences(originalParagraphString, {})
        const words: Array<string> = []
        paragraph.forEach((word: any) => {
          const { necessaryEditIndex, correctText, conceptUID, originalText, currentText, wordIndex, questionUID } = word
          const stringNormalizedCurrentText = stringNormalize(currentText).replace(/\s+/g, ' ').trim()
          const stringNormalizedCorrectText = stringNormalize(correctText)
          const stringNormalizedOriginalText = stringNormalize(originalText)
          const prompt = findSentence(paragraphSentences, wordIndex, stringNormalizedOriginalText)
          if (necessaryEditIndex || necessaryEditIndex === 0) {
            const correct = stringNormalizedCorrectText.split('~').includes(stringNormalizedCurrentText) ? 1 : 0
            numberOfCorrectChanges += correct
            conceptResultsObjects.push({
              metadata: {
                answer: stringNormalizedCurrentText,
                correct,
                instructions: currentActivity.description || this.defaultInstructions(),
                prompt,
                questionNumber: necessaryEditIndex + 1,
                attemptNumber: 1,
                questionScore: correct,
                unchanged: correct ? false : stringNormalizedCurrentText === stringNormalizedOriginalText,
                question_concept_uid: conceptUID,
                question_uid: questionUID
              },
              concept_uid: conceptUID,
              question_type: "passage-proofreader"
            })
            if (correct) {
              words.push(`{+${stringNormalizedCurrentText}-|${conceptUID}}`)
            } else {
              words.push(`{+${correctText}-${stringNormalizedCurrentText}|${conceptUID}}`)
            }
          } else if (stringNormalizedOriginalText !== stringNormalizedCurrentText) {
            const displayedCurrentText = stringNormalizedCurrentText.length ? stringNormalizedCurrentText : ' '
            const unnecessaryEditType = determineUnnecessaryEditType(stringNormalizedOriginalText, stringNormalizedCurrentText)
            if (unnecessaryEditType === UNNECESSARY_SPACE) {
              const wordsToPush = unnecessarySpaceSplitResponse(stringNormalizedOriginalText, stringNormalizedCurrentText)
              wordsToPush.forEach(w => words.push(w))
            } else {
              words.push(`{+${stringNormalizedOriginalText}-${displayedCurrentText}|${unnecessaryEditType}}`)
            }
          } else {
            words.push(stringNormalizedCurrentText)
          }
        })
        reviewablePassage = reviewablePassage.concat('<p>').concat(joinWords(words.filter(word => word.length))).concat('</p>')
      })
      return { reviewablePassage, numberOfCorrectChanges, conceptResultsObjects }
    } else {
      return { reviewablePassage: '', numberOfCorrectChanges: 0, conceptResultsObjects: [] }
    }
  }

  handleCheckWorkClick = () => {
    const { edits, necessaryEdits } = this.state
    const requiredEditCount = necessaryEdits && necessaryEdits.length ? Math.floor(necessaryEdits.length / 2) : 5
    if (necessaryEdits && necessaryEdits.length && edits === 0 || edits < requiredEditCount) {
      this.setState({ showEarlySubmitModal: true })
    } else {
      const { reviewablePassage, numberOfCorrectChanges, conceptResultsObjects } = this.checkWork()
      this.setState({ reviewablePassage, showReviewModal: true, numberOfCorrectChanges, conceptResultsObjects }, this.saveCompletedSessionToFirebase)
    }
  }

  goToFollowupPractice = () => {
    const { firebaseSessionID, } = this.state
    window.location.href = `${window.postbuildEnv.QUILL_GRAMMAR_URL}/play/sw?proofreaderSessionId=${firebaseSessionID}`
  }

  goToLMS = () => window.location.href = `${window.postbuildEnv.DEFAULT_URL}`


  finishReview = () => {
    const { firebaseSessionID, originalPassage, conceptResultsObjects, necessaryEdits, numberOfCorrectChanges } = this.state
    const { admin, dispatch } = this.props
    const activityUID = getParameterByName('uid', window.location.href)
    const newPassage = _.cloneDeep(originalPassage)

    if (admin) {
      this.setState({
        edits: 0,
        reviewing: false,
        showEarlySubmitModal: false,
        showReviewModal: false,
        numberOfCorrectChanges: undefined,
        reviewablePassage: undefined,
        conceptResultsObjects: undefined
      })
      dispatch(setPassage(newPassage))
    } else if (conceptResultsObjects && activityUID) {
      if (necessaryEdits && (necessaryEdits.length === numberOfCorrectChanges)) {
        this.saveToLMS()
      } else if (firebaseSessionID) {
        window.scrollTo(0, 0)
        this.setState({ checkingGrammarQuestions: true }, () => {
          checkIfGrammarQuestionsExistForProofreaderSession(conceptResultsObjects).then((hasQuestions) => {
            if (hasQuestions) {
              this.setState({ checkingGrammarQuestions: false, showFollowupModal: true })
            } else {
              this.saveToLMS()
            }
          })
        })
      }
    }
  }

  onParagraphChange = (value: Array<any>, i: number, editInput: JSX.Element) => {
    const { session, dispatch, } = this.props
    if (session.passage) {
      const newParagraphs = [...session.passage]
      newParagraphs[i] = value
      const caretPosition = EditCaretPositioning.saveSelection(editInput)
      dispatch(setPassage(newParagraphs))
      this.setState({ edits: editCount(newParagraphs), }, () => EditCaretPositioning.restoreSelection(editInput, caretPosition))
    }
  }

  handleNextClick = () => {
    this.setState({ showWelcomePage: false }, () => {
      window.scrollTo(0, 0)
      this.passageContainer && this.passageContainer.focus()
    })
  }

  handleResetClick = () => {
    this.setState({ showResetModal: true })
  }

  closeResetModal = () => {
    this.setState({ showResetModal: false })
  }

  closeEarlySubmitModal = () => {
    this.setState({ showEarlySubmitModal: false })
  }

  closeReviewModal = () => {
    this.setState({ showReviewModal: false, reviewing: true })
  }

  reset = () => {
    const { dispatch, } = this.props
    const { originalPassage, } = this.state
    const newPassage = _.cloneDeep(originalPassage)
    dispatch(setPassage(newPassage))
    this.setState(prevState => ({
      edits: 0,
      numberOfResets: prevState.numberOfResets + 1,
      showResetModal: false
    }))
  }

  renderShowEarlySubmitModal = (): JSX.Element | void => {
    const { showEarlySubmitModal, necessaryEdits } = this.state
    const requiredEditCount = necessaryEdits && necessaryEdits.length ? Math.floor(necessaryEdits.length / 2) : 5
    if (showEarlySubmitModal) {
      return (
        <EarlySubmitModal
          closeModal={this.closeEarlySubmitModal}
          requiredEditCount={requiredEditCount}
        />
      )
    }
  }

  renderShowResetModal = (): JSX.Element | void => {
    const { showResetModal, } = this.state
    if (showResetModal) {
      return (
        <ResetModal
          closeModal={this.closeResetModal}
          reset={this.reset}
        />
      )
    }
  }

  renderFollowupModal = (): JSX.Element | void => {
    const { showFollowupModal, } = this.state
    if (!showFollowupModal) { return }
    return (
      <FollowupModal
        goToFollowupPractice={this.goToFollowupPractice}
        goToLMS={this.goToLMS}
      />
    )
  }

  renderShowReviewModal = (): JSX.Element | void => {
    const { showReviewModal, necessaryEdits, numberOfCorrectChanges } = this.state
    const numberOfErrors = necessaryEdits && necessaryEdits.length ? necessaryEdits.length : 0
    if (showReviewModal) {
      return (
        <ReviewModal
          closeModal={this.closeReviewModal}
          numberOfCorrectChanges={numberOfCorrectChanges || 0}
          numberOfErrors={numberOfErrors}
        />
      )
    }
  }

  renderParagraph = (index: number) => {
    const { session, proofreaderActivities, } = this.props
    const { numberOfResets, } = this.state
    return (
      <Paragraph
        handleParagraphChange={this.onParagraphChange}
        index={index}
        key={index}
        numberOfResets={numberOfResets}
        underlineErrors={proofreaderActivities.currentActivity.underlineErrorsInProofreader}
        words={session.passage[index]}
      />
    )
  }

  renderPassage = (): JSX.Element | void => {
    const { session, proofreaderActivities, concepts, } = this.props
    const { passage } = session
    const { reviewing, reviewablePassage, numberOfResets, showFollowupModal, conceptResultsObjects, edits, } = this.state
    const { underlineErrorsInProofreader } = proofreaderActivities.currentActivity

    // Don't render PassageReviewer when FollowupModal is shown to avoid competing focus traps
    if (reviewing && !showFollowupModal) {
      const text = reviewablePassage ? reviewablePassage : ''
      return (
        <PassageReviewer
          conceptResultsObjects={conceptResultsObjects}
          concepts={concepts.data[0]}
          finishReview={this.finishReview}
          text={text}
        />
      )
    } else if (passage) {
      return <ReadAloudPassageRegion key={numberOfResets} passage={passage} renderParagraph={this.renderParagraph} underlineErrors={underlineErrorsInProofreader} />
    }
  }

  renderCheckWorkButton = (): JSX.Element | void => {
    const { reviewing, edits, } = this.state
    if (reviewing) { return }

    let className = "quill-button large green contained focus-on-light"
    if (edits) {
      return <button className={className} onClick={this.handleCheckWorkClick} type="button">Get feedback</button>
    }

    className += ' disabled'
    return <button className={className} type="button">Get feedback</button>
  }

  renderResetButton = (): JSX.Element | void => {
    const { reviewing, edits, } = this.state
    if (reviewing) { return }

    let className = "quill-button large green outlined focus-on-light"
    if (edits) {
      return <button className={className} onClick={this.handleResetClick} type="button">Undo edits</button>
    }

    className += ' disabled'
    return <button className={className} disabled={true} type="button">Undo edits</button>
  }

  render(): JSX.Element {
    const { activityUID, proofreaderActivities, session, } = this.props
    const { edits, necessaryEdits, loadingFirebaseSession, showWelcomePage, checkingGrammarQuestions, } = this.state
    const { currentActivity } = proofreaderActivities

    if (showWelcomePage) { return <WelcomePage onNextClick={this.handleNextClick} /> }

    if (loadingFirebaseSession || !currentActivity || checkingGrammarQuestions) { return <div className="passage-container" ref={(el) => this.passageContainer = el}><Spinner /></div> }

    if (session.error) { return <div className="passage-container" ref={(el) => this.passageContainer = el}>{session.error}</div> }

    const className = currentActivity.underlineErrorsInProofreader ? 'underline-errors' : ''
    const necessaryEditsLength = necessaryEdits ? necessaryEdits.length : 1
    const meterWidth = edits / necessaryEditsLength * 100
    return (
      <ReadAloudSource source={clipSource(CLIP_SOURCE_TYPE.ACTIVITY, this.activityUid())}>
        <div className="passage-container" ref={(el) => this.passageContainer = el}>
          <section aria-label="Header and directions" className="header-section">
            <ProgressBar answeredQuestionCount={edits} percent={meterWidth} questionCount={necessaryEditsLength} />
            <div className="inner-header">
              <ReadAloudContent label="Read the title aloud"><h1>{currentActivity.title}</h1></ReadAloudContent>
              <div className="instructions">
                <img alt="" src={`${SMALL_ICON_BASE_SRC}/information.svg`} />
                <ReadAloudHtml element="p" html={currentActivity.description || this.defaultInstructions()} label="Read the directions aloud" />
                <p className="sr-only">Screenreader users: once you have finished reading the passage, use the tab keys to navigate between words and make changes to ones that have errors. {currentActivity.underlineErrorsInProofreader && 'Words that contain errors will be described as underlined.'} Words that you have already changed will be described as bolded. There are {necessaryEditsLength} errors to find and fix. When you are done, navigate to the &#34;Get Feedback&#34; button after the passage and select it.</p>
              </div>
              <ReadAloudNote />
            </div>
          </section>
          {this.renderShowEarlySubmitModal()}
          {this.renderShowResetModal()}
          {this.renderShowReviewModal()}
          {this.renderFollowupModal()}
          <section aria-label="Passage" className={`passage ${className}`}>
            {this.renderPassage()}
          </section>
          <section aria-label="Passage buttons" className="bottom-section">
            <div id="button-container" tabIndex={-1}>
              {this.renderResetButton()}
              {this.renderCheckWorkButton()}
            </div>
          </section>
        </div>
      </ReadAloudSource>
    )
  }
}

const mapStateToProps = (state: any) => {
  return {
    proofreaderActivities: state.proofreaderActivities,
    session: state.session,
    concepts: state.concepts
  };
};

const mapDispatchToProps = (dispatch: Redux.Dispatch<any>) => {
  return {
    dispatch
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(PlayProofreaderContainer);
